import { useState, useRef, useCallback } from 'react';
import { RefreshCw } from 'lucide-react';

/**
 * PullToRefresh - Pull down to refresh like native mobile apps
 * Only active on touch devices when at the top of the page
 */
const PullToRefresh = ({ children, onRefresh, threshold = 100, className = '' }) => {
    const [pullDistance, setPullDistance] = useState(0);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const startY = useRef(0);
    const isPulling = useRef(false);
    const containerRef = useRef(null);

    // Check if we're at the top of the scrollable container or page
    const isAtTop = useCallback(() => {
        if (containerRef.current) {
            const parent = containerRef.current.closest('.overflow-auto, .overflow-y-auto');
            if (parent) {
                return parent.scrollTop === 0;
            }
        }
        return window.scrollY === 0;
    }, []);

    const handleTouchStart = useCallback((e) => {
        if (isRefreshing) return;

        // Only start pull if at top
        if (isAtTop()) {
            startY.current = e.touches[0].clientY;
            isPulling.current = true;
        }
    }, [isAtTop, isRefreshing]);

    const handleTouchMove = useCallback((e) => {
        if (!isPulling.current || isRefreshing) return;

        const currentY = e.touches[0].clientY;
        const diff = currentY - startY.current;

        // Only pull down, not up, and only when at top
        if (diff > 0 && isAtTop()) {
            // Apply resistance - the further you pull, the harder it gets
            const resistance = 0.5;
            const distance = Math.min(diff * resistance, threshold * 1.5);
            setPullDistance(distance);
        } else if (diff <= 0) {
            // Reset if scrolling up
            setPullDistance(0);
            isPulling.current = false;
        }
    }, [isAtTop, isRefreshing, threshold]);

    const handleTouchEnd = useCallback(() => {
        if (!isPulling.current) return;
        isPulling.current = false;

        if (pullDistance >= threshold && !isRefreshing) {
            // Trigger refresh
            setIsRefreshing(true);
            setPullDistance(50); // Keep spinner visible

            // Reload page after a short delay
            setTimeout(() => {
                if (onRefresh) {
                    onRefresh();
                } else {
                    window.location.reload();
                }
            }, 300);
        } else {
            // Reset
            setPullDistance(0);
        }
    }, [pullDistance, threshold, isRefreshing, onRefresh]);

    // Calculate progress (0 to 1)
    const progress = Math.min(pullDistance / threshold, 1);
    const showIndicator = pullDistance > 10 || isRefreshing;

    return (
        <div
            ref={containerRef}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            className={`relative h-full w-full ${className}`}
        >
            {/* Pull indicator */}
            {showIndicator && (
                <div
                    className="absolute left-1/2 -translate-x-1/2 z-50 pointer-events-none"
                    style={{
                        top: `${Math.max(pullDistance - 50, 10)}px`,
                        opacity: progress,
                        transition: isPulling.current ? 'none' : 'all 0.2s ease-out',
                    }}
                >
                    <div
                        className={`w-10 h-10 rounded-full bg-white shadow-lg flex items-center justify-center border border-gray-200 ${
                            isRefreshing ? 'animate-spin' : ''
                        }`}
                        style={{
                            transform: isRefreshing ? 'none' : `rotate(${progress * 360}deg)`,
                        }}
                    >
                        <RefreshCw
                            className="w-5 h-5"
                            style={{
                                color: progress >= 1 || isRefreshing ? '#9333ea' : '#9ca3af',
                            }}
                        />
                    </div>
                </div>
            )}

            {/* Content */}
            <div
                className="h-full w-full"
                style={{
                    transform: pullDistance > 0 ? `translateY(${pullDistance}px)` : 'none',
                    transition: isPulling.current ? 'none' : 'transform 0.2s ease-out',
                }}
            >
                {children}
            </div>
        </div>
    );
};

export default PullToRefresh;
